/**
 * Sporer hvilke asset-paths backend har bekreftet finnes i library, og polles
 * /signage/library mens noen er pending. Erstatter URL-polling-with-404-retry
 * i d-loading-video og d-loading-image (som spamte serverlogger og hadde
 * inkonsistent state).
 *
 * Pendant-mønster til pending-uploads.ts: rein in-memory state, lever per
 * tab/sesjon. Backend er sannhetskilde — vi reflekterer bare når library
 * faktisk har bilde/video klart.
 *
 * Komponenter (d-loading-video, d-loading-image, d-edit-slide-dialog,
 * d-media-gallery) subscribe per path og får `ready=true` så snart polling
 * oppdager pathen i library-svar. Service starter polling lazy (idempotent
 * per orgId) og stopper automatisk når pending-settet er tomt for orgId-en.
 */
import { fetchLibrary } from '../store/signage';

const POLL_INTERVAL_MS = 3000;

type Subscriber = (ready: boolean) => void;

/**
 * `std/...`-paths peker til standard-library som deployes av
 * `sync-standard-library.sh` og er statisk innhold (også i undermapper som
 * `std/istock/...` og `std/kloke-valg/...`). Disse trenger aldri polling —
 * de er trygge å laste umiddelbart. Polling er kun for org-uploads som
 * venter på konvertering. Se signage/DESIGN.md.
 */
function isStandardLibraryPath(path: string): boolean {
  return path.startsWith('std/');
}

class AssetAvailabilityService {
  private knownPaths = new Set<string>();
  private pendingByOrg = new Map<number, Set<string>>();
  private pollers = new Map<number, number>();
  private subs = new Map<string, Set<Subscriber>>();

  /**
   * Registrerer paths som backend har bekreftet finnes. Kalles med
   * library-snapshot fra buildScreenView og fra polleren her.
   */
  markKnownPaths(paths: Iterable<string>): void {
    for (const p of paths) {
      if (!p || this.knownPaths.has(p)) continue;
      this.knownPaths.add(p);
      for (const set of this.pendingByOrg.values()) set.delete(p);
      this.emit(p, true);
    }
    this.gcPollers();
  }

  /**
   * Marker at vi venter på at en path skal dukke opp i library, og start
   * polling for orgId-en hvis ikke allerede aktiv. Idempotent.
   */
  awaitPath(orgId: number, path: string): void {
    if (!path || this.knownPaths.has(path) || isStandardLibraryPath(path)) return;
    let set = this.pendingByOrg.get(orgId);
    if (!set) {
      set = new Set();
      this.pendingByOrg.set(orgId, set);
    }
    set.add(path);
    this.startPolling(orgId);
  }

  isReady(path: string): boolean {
    return !path || this.knownPaths.has(path) || isStandardLibraryPath(path);
  }

  /**
   * Subscribe på en path — emitter umiddelbart med nåværende state, og deretter
   * når den blir bekreftet. Returnerer unsubscribe-funksjon.
   */
  subscribe(path: string, cb: Subscriber): () => void {
    if (!path || isStandardLibraryPath(path)) {
      cb(true);
      return () => {};
    }
    let set = this.subs.get(path);
    if (!set) {
      set = new Set();
      this.subs.set(path, set);
    }
    set.add(cb);
    cb(this.knownPaths.has(path));
    return () => {
      set!.delete(cb);
      if (set!.size === 0) this.subs.delete(path);
    };
  }

  private emit(path: string, ready: boolean): void {
    const set = this.subs.get(path);
    if (set) set.forEach((cb) => cb(ready));
  }

  private startPolling(orgId: number): void {
    if (this.pollers.has(orgId)) return;
    const tick = async () => {
      this.pollers.delete(orgId);
      const pending = this.pendingByOrg.get(orgId);
      if (!pending || pending.size === 0) return;
      try {
        const lib = await fetchLibrary(orgId);
        const paths: string[] = [];
        for (const item of lib) {
          for (const a of item.assets ?? []) paths.push(a);
        }
        this.markKnownPaths(paths);
      } catch (err) {
        console.warn('[asset-availability] poll failed', err);
      }
      const stillPending = this.pendingByOrg.get(orgId);
      if (stillPending && stillPending.size > 0) {
        this.pollers.set(orgId, window.setTimeout(tick, POLL_INTERVAL_MS));
      }
    };
    this.pollers.set(orgId, window.setTimeout(tick, POLL_INTERVAL_MS));
  }

  private gcPollers(): void {
    for (const [orgId, set] of this.pendingByOrg.entries()) {
      if (set.size === 0) {
        this.pendingByOrg.delete(orgId);
        const timer = this.pollers.get(orgId);
        if (timer !== undefined) {
          window.clearTimeout(timer);
          this.pollers.delete(orgId);
        }
      }
    }
  }
}

export const assetAvailability = new AssetAvailabilityService();
